import cn from 'classnames';
import styles from './InstitutionLogoContent.module.css';
import { TInstitutionServerCamel } from '@/types';
import { checkIsKiosk } from '@/utils/kiosk';
import { FlatButton } from '@globalmed/atlas-ui';
import { useTranslation } from 'react-i18next';

type TProps = {
  isPreferBanner?: boolean;
  size?: 'sm' | 'lg';
  onClick?: () => void;
  whitelabel?: TInstitutionServerCamel['whitelabel'];
};

/**
 * A component that displays the institution's logo or banner based on whitelabel settings.
 * Automatically selects the appropriate display format (banner, square logo, or avatar)
 * based on available assets and configuration. In kiosk routes the logo is always round
 * (banner layout is not used; logo URL falls back to banner image if needed).
 * @param props - The component props.
 * @param props.isPreferBanner - Whether to prefer displaying the banner over other formats when available. Defaults to true.
 * @param props.size - The size of the logo/banner. Can be 'sm' or 'lg'. Defaults to 'lg'.
 * @param props.whitelabel - The white-label configuration data, including logo/banner URLs and shape settings.
 * @param props.onClick - Optional callback function to be called when the logo/banner is clicked.
 * @returns A logo component that renders one of three formats.
 */
export const InstitutionLogoContent = ({
  isPreferBanner = true,
  size = 'lg',
  whitelabel,
  onClick,
}: TProps) => {
  const { t } = useTranslation();
  const getLogoConfig = () => {
    if (!whitelabel?.enabled)
      return { src: '/logo.png', className: styles.round };

    if (checkIsKiosk()) {
      const src = whitelabel?.logoImageUrl ?? '/logo.png';
      return { src, className: styles.round };
    }

    if (isPreferBanner && !!whitelabel?.bannerImageUrl)
      return { src: whitelabel.bannerImageUrl, className: styles.banner };

    return {
      src: whitelabel?.logoImageUrl ?? '/logo.png',
      className: styles[whitelabel?.logoImageShape ?? 'round'],
    };
  };

  const { src, className } = getLogoConfig();
  const rootClassName = cn(className, styles.logo, styles[size]);
  const image = <img src={src} alt={t('institution-logo')} />;

  if (onClick)
    return (
      <FlatButton
        className={rootClassName}
        onClick={onClick}
        aria-label={t('institution-logo')}
      >
        {image}
      </FlatButton>
    );

  return <div className={rootClassName}>{image}</div>;
};
